import React from 'react';
import clsx from 'clsx';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'purple' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  dot?: boolean;
  pulse?: boolean;
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  pulse = false,
  className,
}) => {
  const variants = {
    default: 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] border-[var(--border-color)]',
    success: 'bg-success-500/20 text-success-400 border-success-500/30',
    warning: 'bg-warning-500/20 text-warning-400 border-warning-500/30',
    error: 'bg-error-500/20 text-error-400 border-error-500/30',
    info: 'bg-accent-500/20 text-accent-400 border-accent-500/30',
    purple: 'bg-primary-500/20 text-primary-400 border-primary-500/30',
    secondary: 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border-[var(--border-color)]',
    danger: 'bg-error-500/20 text-error-400 border-error-500/30',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm',
  };

  const dotColors = {
    default: 'bg-[var(--text-muted)]',
    success: 'bg-success-400',
    warning: 'bg-warning-400',
    error: 'bg-error-400',
    info: 'bg-accent-400',
    purple: 'bg-primary-400',
    secondary: 'bg-[var(--text-muted)]',
    danger: 'bg-error-400',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 font-medium rounded-full border',
        variants[variant],
        sizes[size],
        className
      )}
    >
      {dot && (
        <span className="relative flex h-2 w-2">
          {pulse && (
            <span
              className={clsx(
                'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
                dotColors[variant]
              )}
            />
          )}
          <span
            className={clsx('relative inline-flex rounded-full h-2 w-2', dotColors[variant])}
          />
        </span>
      )}
      {children}
    </span>
  );
};

export default Badge;
